import React, { useRef } from "react";

import { AddCircleOutline as AddCircleOutlineIcon } from "@mui/icons-material";
import { ImageList, ImageListItem, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";

import { useActualInventoryContext } from "Hooks/useActualInventoryContext";
import { Picture } from "Models/picture";
import { ItemJob } from "Models/sales";
import { convertEventToFile } from "Util/convertEventToFile";
import { base, BASE64_REGEX } from "Util/convertToBase64";
import { FileType } from "Util/enums";
import Button, { Color } from "Components/Form/Button";
import ImageCarouselModal, { ImageCarouselModalHandle, File } from "Components/ImageCarouselModal";
import ImageLoading from "Components/Generic/ImageLoading";
import { NotificationToast } from "Components/Generic/Toast/NotificationToast";
import useStyles, { EmptyPhotoContainer, ImageContainer, NoPhotosAddedYetSpan } from "./styles";
const emptyPhoto = require("../../../../../../assets/img/empty-photos.svg");

const Photos = () => {
  const { t: translate } = useTranslation();

  const inputRef = useRef<HTMLInputElement>(null);
  const imageCarouselModalRef = useRef<ImageCarouselModalHandle>(null);

  const { itemJob, setItemJob } = useActualInventoryContext();

  const classes = useStyles();

  const handleFileRead = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = await convertEventToFile(event, translate, ["jpg", "png", "jpeg"]);

    if (file && file?.uri) {
      const base64Splitted = file?.uri?.split(";base64,");

      if (base64Splitted && base64Splitted?.length > 0 && base64Splitted[1]) {
        const itemJobUpdated = {
          ...(itemJob || {}),
          pictures: [...(itemJob?.pictures || []), { mediaLink: base64Splitted[1] }],
        } as ItemJob;

        setItemJob(itemJobUpdated);
        NotificationToast.success(translate("photoAddedSuccessfully"), {
          id: "actual.inventory.photo.add",
        });
      }
    }
    event.target.value = "";
  };

  const handleRemove = (picture: Picture, index: number) => {
    let itemJobUpdated = { ...(itemJob || {}) };

    if (itemJobUpdated?.pictures && itemJobUpdated?.pictures?.length > 0) {
      delete itemJobUpdated.pictures[index];
      itemJobUpdated.pictures = itemJobUpdated.pictures.filter(Boolean);
      if (picture?.id) {
        itemJobUpdated = {
          ...itemJobUpdated,
          picturesDeleted: [...(itemJobUpdated?.picturesDeleted || []), picture],
        };
      }
      setItemJob(itemJobUpdated);
    }
  };

  const renderPhotos = () => {
    if (itemJob && itemJob?.pictures && itemJob?.pictures?.length > 0) {
      return (
        <ImageList className={classes.imageList} cols={3}>
          {itemJob?.pictures?.map((picture, index) => {
            if (picture && picture?.mediaLink)
              return (
                <ImageListItem key={picture?.mediaLink}>
                  <ImageLoading
                    src={
                      BASE64_REGEX.test(picture?.mediaLink)
                        ? `${base()}${picture?.mediaLink}`
                        : picture?.mediaLink
                    }
                    skeleton={{ className: classes.skeleton }}
                    className={classes.image}
                    onRemove={() => handleRemove(picture, index)}
                    onImageClick={() => onOpenImgModal(index)}
                  />
                </ImageListItem>
              );

            return <></>;
          })}
        </ImageList>
      );
    }

    return (
      <EmptyPhotoContainer>
        <img src={emptyPhoto} />
        <NoPhotosAddedYetSpan>{translate("noPhotosHaveBeenAddedYet")}</NoPhotosAddedYetSpan>
      </EmptyPhotoContainer>
    );
  };

  const onOpenImgModal = (imgIndex: number) => {
    let pictureFiles: File[] = [];
    const { pictures } = itemJob!;

    if (pictures) {
      pictureFiles = pictures.map(
        (picture) =>
          ({
            url: BASE64_REGEX.test(picture?.mediaLink ?? "")
              ? `${base()}${picture?.mediaLink}`
              : picture?.mediaLink ?? "",
            type: FileType.IMAGE,
          } as File)
      );
    }

    imageCarouselModalRef?.current?.handleOpenModal(imgIndex, pictureFiles);
  };

  return (
    <div>
      <input
        type="file"
        accept=".jpeg,.png,.jpg"
        className={classes.inputFile}
        ref={inputRef}
        onChange={handleFileRead}
      />
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <span className={classes.title}>{translate("photos")}</span>
        <Button
          startIcon={<AddCircleOutlineIcon htmlColor="#FFFFFF" />}
          customColor={Color.SECONDARY}
          onClick={() => inputRef?.current?.click()}
        >
          {translate("addAPhoto")}
        </Button>
      </Stack>
      <ImageContainer>{renderPhotos()}</ImageContainer>
      <ImageCarouselModal ref={imageCarouselModalRef} />
    </div>
  );
};

export default Photos;
